/**
 * Admin client for the Retailers workspace tree — /api/admin-retailer-tree.php.
 * One payload: every retailer node (parent → sub-retailers) with its counts, plus
 * how many stores are unassigned. Credentialed (admin session). English-only UI.
 */

import type { RetailerFacets } from './adminLocationMapsApi';
import { fetchMapFacets } from './adminLocationMapsApi';

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

export interface RetailerTreeNode {
  id: number;
  name: string;
  parentRetailerId: number | null;
  isActive: boolean;
  isPartner: boolean;
  subdomain: string | null;
  pricingSource: 'own' | 'parent';
  brandIconUrl: string | null;
  counts: { locations: number; people: number; waiting: number };
  /** Top-level retailers only: own + sub-retailers, each person counted once. */
  familyCounts?: { locations: number; people: number; waiting: number };
}

export interface RetailerTreeResponse {
  nodes: RetailerTreeNode[];
  unassigned: { locations: number; people: number };
}

export async function fetchRetailerTree(): Promise<RetailerTreeResponse> {
  const res = await fetch(`${API_BASE}/admin-retailer-tree.php`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || `Request failed (${res.status})`);
  return data as RetailerTreeResponse;
}

/** The banners (buying groups) trusscore.com knows, with store counts — for the Location Finder. */
export async function fetchBanners(): Promise<{ banners: { value: string; count: number }[]; countries: { code: string; name: string; count: number }[]; stale: boolean }> {
  const r = await fetchMapFacets('retailers');
  if ('notConfigured' in r && r.notConfigured) return { banners: [], countries: [], stale: false };
  const f = (r as { facets: RetailerFacets; stale: boolean });
  return { banners: f.facets.buyingGroups ?? [], countries: f.facets.countries ?? [], stale: !!f.stale };
}
